import { fromEvent, partition, merge, filter, map, EMPTY, of  } from 'rxjs';
import fastdom from 'fastdom';
import { debounceTime, switchMap } from 'rxjs/operators';
import StringExtensions from 'utils/stringExtensions';
import getTypeaheadSuggestions from 'api/getTypeaheadSuggestions';
import typeaheadKeyboardHandler from './typeaheadKeyboardHandler';

class Typeahead {

  getSourceUrl(searchTerm) {
    const separator = this.sourceUrl.includes('?') ? '&' : '?';
    return `${this.sourceUrl}${separator}searchTerm=${encodeURIComponent(searchTerm)}`;
  }

  displayErrorMessage() {
    this.errorContainer.removeAttribute('hidden');
  }
  
  setInitialSearchInputValue() {
    // setting the value on initial load if available
    // we need to set the value using javascript because if the value is set on the HTML
    // it will be set as the input's default value, preventing the user from clearing the input
    // since the X icon is a button[type='reset'] which will reset the input to its default value.
    // for more: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/reset
    if (this.searchInput?.dataset?.value) {
      this.searchInput.value = this.searchInput.dataset.value;
    }
  }

  closeErrorMessage() {
    this.errorContainer.setAttribute('hidden', '');
  }

  clearSuggestions() {
    this.suggestionsList.innerHTML = '';
    this.suggestionsList.setAttribute('hidden', '');    
  }

  clearSearchInput() {
    this.searchInput.value = '';
  }

  autoSelectSuggestion() {
    const searchInputValue = this.searchInput?.value.trim() ?? '';
    
    // redirect when the value is in the suggestion list
    const suggestedOption = this.suggestionsList.querySelector(`[value="${searchInputValue}"]`);
    suggestedOption.click();
  }

  displaySuggestions(suggestions) {
    const fragment = document.createDocumentFragment();

    suggestions.forEach(suggestion => {
      const content = suggestion[this.suggestionValueKey];
      const li = document.createElement('li');

      // value for typeahead keyboard handler
      li.setAttribute('value', content);

      // value to display on suggestion item
      li.textContent = content;

      const keysArray = Object.keys(suggestion);
      keysArray.forEach((key) => {
        const value = suggestion[key];
        if(value) {
          const kebabCaseKey = StringExtensions.camelToKebabCase(key);
          li.setAttribute(`data-${kebabCaseKey}`, value);
        }
      });

      fragment.appendChild(li);
    });

    fastdom.mutate(() => {
      this.suggestionsList.appendChild(fragment);
      this.suggestionsList.removeAttribute('hidden');
      this.suggestionsList.scrollTop = 0;
    });
  }

  setSearchResultEvent(){
    if(this.searchInput && this.suggestionsList && this.errorContainer){
      const searchInput$ = fromEvent(this.searchInput, 'input');
      const searchFocus$ = fromEvent(this.searchInput, 'focus');
      const searchEvent$ = merge(searchInput$, searchFocus$);
      this.searchResult$ = searchEvent$.pipe(
        debounceTime(100),
        switchMap((event) => {
          this.clearSuggestions();
          this.closeErrorMessage();
          const userInput = event.target.value;
          const trimmedUserInput = userInput?.trim();
          if (!trimmedUserInput) {
            return [];
          }

          return getTypeaheadSuggestions(this.getSourceUrl(trimmedUserInput));
        })
      );

      const [displaySuggestions$, displayErrorMessage$] = partition(
        this.searchResult$, (list) => list.length > 0
      );

      displaySuggestions$.subscribe(this.displaySuggestions.bind(this));
      displayErrorMessage$.subscribe(this.displayErrorMessage.bind(this));
    }
  }

  setSuggestionListClickedEvent(){
    if(this.suggestionsList && this.searchInput){
      this.suggestionListClicked$ = fromEvent(this.suggestionsList, 'click').pipe(
        filter((event) => event.target.closest('li')),
        map((event) => event.target.closest('li'))
      );

      this.suggestionListClicked$.subscribe((suggestion) => {
        this.searchInput.value = suggestion.innerText.trim();
      });
    }
  }

  setSubmitButtonClickedEvent(){
    if(this.submitButton){

      const submitButtonClicked$ = fromEvent(this.submitButton, 'click');
      const enterKeydown$ = fromEvent(this.searchInput, 'keydown').pipe(
        filter((event) => event.key === 'Enter')
      );

      const autoSelectSuggestion$ = merge(enterKeydown$, submitButtonClicked$).pipe(
        switchMap((event) => {
          event.preventDefault();
          const searchTerm = this.searchInput?.value.trim() ?? '';
          return of(searchTerm);
        }),
        filter((searchTerm) => searchTerm !== '')   
      );

      const [selectSuggestion$, submitSuggestion$] = partition(
        autoSelectSuggestion$, (searchTerm) => !!this.suggestionsList.querySelector(`[value="${searchTerm}"]`)
      );

      selectSuggestion$.subscribe(this.autoSelectSuggestion.bind(this));

      this.submitEventObserver$ = submitSuggestion$;
    }
  }

  setClearButtonClickedEvent(){
    if(this.clearButton && this.suggestionsList && this.searchInput){
      const clearButtonClicked$ = fromEvent(this.clearButton, 'click');
      clearButtonClicked$.subscribe(() => {
        this.closeErrorMessage();
        this.clearSuggestions();
        this.clearSearchInput();
      });
    }
  }

  setInputOutClickedEvent(){
    if(this.searchInput && this.suggestionsList){

      // add debounce of 500 to avoid delete the suggestion list before to perform another operation
      const inputBlur$ = fromEvent(this.searchInput, 'blur').pipe(debounceTime(500));
      inputBlur$.subscribe(this.clearSuggestions.bind(this));
    }
  }

  setkeyDownHandler(){
    if(this.searchInput && this.suggestionsList){
      const keyDownHandler = typeaheadKeyboardHandler({
        searchInput: this.searchInput,
        suggestList: this.suggestionsList,
        selectedOptionIndex: -1,
        searchObserver: this.searchResult$
      });

      const keydown$ = fromEvent(this.searchInput, 'keydown').pipe(
        filter((event) => event.key !== 'Enter')
      );

      keydown$.subscribe(keyDownHandler);
    }
  }

  setTypeaheadSetting(setting){
    this.searchInput = setting.searchInputQuerySelector
      ? document.querySelector(setting.searchInputQuerySelector) : undefined;
    this.suggestionsList = setting.suggestionsListQuerySelector
      ? document.querySelector(setting.suggestionsListQuerySelector) : undefined;
    this.errorContainer = setting.errorContainerQuerySelector
      ? document.querySelector(setting.errorContainerQuerySelector) : undefined;
    this.submitButton = setting.submitButtonQuerySelector
      ? document.querySelector(setting.submitButtonQuerySelector) : undefined;
    this.clearButton = setting.clearButtonQuerySelector
      ? document.querySelector(setting.clearButtonQuerySelector) : undefined;

    this.sourceUrl = setting.sourceUrl;
    this.suggestionValueKey = setting.suggestionValueKey;

    this.setSearchResultEvent();
    this.setSuggestionListClickedEvent();
    this.setClearButtonClickedEvent();
    this.setSubmitButtonClickedEvent();
    this.setInputOutClickedEvent();
    this.setkeyDownHandler();
    this.setInitialSearchInputValue();
  }

  getSuggestionListClickedObserver(){
    // To avoid null references
    return this.suggestionListClicked$ ?? EMPTY;
  }

  getSubmitEventObserver(){
    // To avoid null references
    return this.submitEventObserver$ ?? EMPTY;
  }

  getSearchResultObserver(){
    // To avoid null references
    return this.searchResult$ ?? EMPTY;
  }
}

export default Typeahead;